import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { toast } from "sonner";
import { AnnouncementsCard } from "@/components/dashboard/announcements-card";
import { DashboardKpiStrip } from "@/components/dashboard/dashboard-widgets";
import { DashboardTodayCard, buildClinicOptions } from "@/components/dashboard/dashboard-today-card";
import { DashboardWhosInCard } from "@/components/dashboard/dashboard-whos-in-card";
import { DeletePostDialog } from "@/components/dashboard/delete-post-dialog";
import { PostEditorDialog } from "@/components/dashboard/post-editor-dialog";
import { deleteDashboardPost } from "@/lib/api/dashboard-posts";
import { fetchTenants } from "@/lib/api/tenants";
import { getAuthUser } from "@/lib/api/token-store";
import { usePermissions } from "@/hooks/use-permissions";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import { withAppTimeZone } from "@/lib/timezone";
import { cn } from "@/lib/utils";
import { hasPermission } from "@/lib/stores/permissions-store";
import { setUserPreferences } from "@/lib/stores/user-preference-store";
import {
  getDashboardPostsState,
  markDashboardSeen,
  refreshDashboardPosts,
  subscribeDashboardPosts,
} from "@/lib/stores/dashboard-posts-store";

function ownerMatches(ownerUserId) {
  return String(getAuthUser()?.id || "").trim() === String(ownerUserId || "").trim();
}

function formatRelativeTime(iso) {
  const ts = Date.parse(String(iso || ""));
  if (!Number.isFinite(ts)) return "";
  const minutes = Math.floor((Date.now() - ts) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString(undefined, withAppTimeZone());
}

function formatAbsoluteTime(iso) {
  const ts = Date.parse(String(iso || ""));
  if (!Number.isFinite(ts)) return "";
  return new Date(ts).toLocaleString(undefined, withAppTimeZone());
}

function greetingWord(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function firstNameOf(user) {
  const profileFirst = String(user?.profile?.firstName || "").trim();
  if (profileFirst) return profileFirst;
  const name = String(user?.name || user?.displayName || "").trim();
  return name.split(/\s+/)[0] || "there";
}

/**
 * The signed-in Dashboard, "Workbench" layout: greeting + KPI strip header row over two
 * columns — the announcements feed (main workspace) and a right rail with the
 * clinic-scoped Today timeline and the Who's-in presence card.
 *
 * Visit semantics are unchanged: the mount effect (re-fired by <Activity> on every
 * re-show) captures the PRIOR seen stamp into pane-local `highlightSince`, then stamps
 * "seen now" — NEW badges persist for the visit, the red nav pip clears immediately.
 * Everything is owner-guarded: this pane also renders inside frozen workspaces during
 * switch overlap.
 */
export function DashboardContent({ ownerUserId }) {
  const [feed, setFeed] = useState(() => getDashboardPostsState());
  // undefined = visit effect not yet run (mark nothing), null = first-ever visit (mark
  // everything), string = the prior stamp.
  const [highlightSince, setHighlightSince] = useState(undefined);
  const [editorState, setEditorState] = useState({ open: false, post: null });
  const [deleteState, setDeleteState] = useState({ open: false, post: null, inProgress: false });
  const [clinics, setClinics] = useState([]);

  // Reactive permissions: the Today card appears/disappears with calendar rights.
  usePermissions();
  const selectedClinicId = useUserPreferences().dashboardClinicId;

  useEffect(
    () =>
      subscribeDashboardPosts((next) => {
        if (!ownerMatches(ownerUserId)) return;
        setFeed(next);
      }),
    [ownerUserId],
  );

  // The visit: stamp seen, keep the prior threshold for this pane's NEW badges, refetch.
  useEffect(() => {
    if (!ownerMatches(ownerUserId)) return;
    const prior = markDashboardSeen();
    setHighlightSince(prior ?? null);
    setFeed(getDashboardPostsState());
    void refreshDashboardPosts();
  }, [ownerUserId]);

  // Clinic directory for the Today card + header subtitle. Employee-wide vocabulary
  // endpoint; failure just leaves the selector empty.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetchTenants();
        if (cancelled || !ownerMatches(ownerUserId)) return;
        setClinics(buildClinicOptions(response.items));
      } catch {
        if (!cancelled) setClinics([]);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [ownerUserId]);

  // Single-clinic users get their only clinic auto-selected (persisted); multi-clinic
  // users must choose deliberately — schedules are never mixed or guessed.
  useEffect(() => {
    if (!ownerMatches(ownerUserId)) return;
    if (clinics.length === 1 && !selectedClinicId) {
      setUserPreferences({ dashboardClinicId: clinics[0].id });
    }
  }, [clinics, selectedClinicId, ownerUserId]);

  const isNew = (post) => {
    if (highlightSince === undefined) return false;
    const created = Date.parse(String(post?.createdAt || ""));
    if (!Number.isFinite(created)) return false;
    if (highlightSince === null) return true;
    const since = Date.parse(String(highlightSince));
    return !Number.isFinite(since) || created > since;
  };

  const handleDelete = async () => {
    const post = deleteState.post;
    if (!post) return;
    setDeleteState((prev) => ({ ...prev, inProgress: true }));
    try {
      await deleteDashboardPost(post.id);
      toast.success("Announcement deleted");
      setDeleteState({ open: false, post: null, inProgress: false });
    } catch (error) {
      toast.error(error?.message || "Could not delete the announcement");
      setDeleteState({ open: false, post: null, inProgress: false });
    }
    void refreshDashboardPosts();
  };

  const { greeting, dateLabel } = useMemo(() => {
    const now = DateTime.now();
    return { greeting: greetingWord(now.hour), dateLabel: now.toFormat("cccc d LLLL") };
    // Recomputed on every pane show (the component remounts); no live tick needed.
  }, []);
  const firstName = firstNameOf(getAuthUser());
  const selectedClinic = clinics.find((clinic) => clinic.id === selectedClinicId) || null;
  const showTodayCard = hasPermission("calendar", "view");

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-[18px] overflow-hidden p-6 min-[1440px]:px-[34px] min-[1440px]:py-[30px]">
      <div className="flex flex-none flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div>
          <h1 className="text-[25px] font-black leading-tight tracking-[-0.3px] text-foreground">
            {greeting}, {firstName}
          </h1>
          <div className="mt-1 text-[13.5px] text-muted-foreground">
            {dateLabel}
            {selectedClinic ? ` · ${selectedClinic.label}` : ""}
          </div>
        </div>
        <DashboardKpiStrip ownerUserId={ownerUserId} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-[18px] min-[1200px]:flex-row">
        <AnnouncementsCard
          feed={feed}
          isNew={isNew}
          // The same visit stamp the NEW badges use: a thread with comments you haven't seen
          // opens by default, however long it has grown.
          highlightSince={highlightSince}
          formatRelativeTime={formatRelativeTime}
          formatAbsoluteTime={formatAbsoluteTime}
          onNewPost={() => setEditorState({ open: true, post: null })}
          onEditPost={(post) => setEditorState({ open: true, post })}
          onDeletePost={(post) => setDeleteState({ open: true, post, inProgress: false })}
        />

        <div
          className={cn(
            // One 434px column at desktop; between 640 and 1199px the rail sits under the
            // announcements with its two cards side by side; below that everything stacks.
            "flex min-h-0 w-full flex-none flex-col gap-[18px] sm:max-[1199px]:flex-row min-[1200px]:w-[434px]",
          )}
        >
          {showTodayCard && (
            <DashboardTodayCard
              ownerUserId={ownerUserId}
              clinics={clinics}
              selectedClinicId={selectedClinicId}
            />
          )}
          <DashboardWhosInCard ownerUserId={ownerUserId} />
        </div>
      </div>

      <PostEditorDialog
        open={editorState.open}
        post={editorState.post}
        onOpenChange={(open) => setEditorState((prev) => ({ ...prev, open }))}
        onSaved={() => void refreshDashboardPosts()}
      />
      <DeletePostDialog
        open={deleteState.open}
        postTitle={deleteState.post?.title}
        inProgress={deleteState.inProgress}
        onOpenChange={(open) =>
          setDeleteState((prev) => (prev.inProgress ? prev : { ...prev, open }))
        }
        onConfirmYes={handleDelete}
      />
    </div>
  );
}
